import { createContext, useContext } from 'react';
import type { SupportAccessGrant, SupportAccessGrantList } from '@traceio-org/platform-client';
import type { AdminOrganizationDetail } from '@traceio-org/platform-contracts';
import type { StaffAbilities } from '../../permissions';
import type { AdminRead } from '../../useAdminRead';

/** What every tab of an organization shares: the organization, who is looking, and their support access. */
export interface OrganizationScope {
  organization: AdminOrganizationDetail;
  abilities: StaffAbilities;
  /** The signed-in staff member's user id (their own support access grants carry it). */
  staffUserId: string;
  /** The support access grants listed for this organization: the caller's, every staff member's for platform admins. */
  grants: AdminRead<SupportAccessGrantList>;
  /** The caller's own grant for this organization in force now, if any. */
  activeGrant: SupportAccessGrant | null;
  /**
   * True when a refused read may be refused for lack of support access: the caller is not a platform admin and
   * holds no grant in force. It only words the explanation; platform-api decides.
   */
  mayNeedSupportAccess: boolean;
  /** Path of a tab of this organization ('' for the overview). */
  tabPath: (tab: string) => string;
  /** Reads the organization again after a change to it (the current one stays on screen meanwhile). */
  reloadOrganization: () => void;
}

const OrganizationScopeContext = createContext<OrganizationScope | null>(null);

export const OrganizationScopeProvider = OrganizationScopeContext.Provider;

export function useOrganizationScope(): OrganizationScope {
  const scope = useContext(OrganizationScopeContext);
  if (!scope) throw new Error('useOrganizationScope() works only inside an organization page');
  return scope;
}

/**
 * How far the browser's clock may lag platform-api's: a grant platform-api has just started (its startsAt is the
 * server's now) counts as started. Only the start is lenient; a grant ends when the browser's clock says so.
 */
export const CLOCK_SKEW_MS = 120_000;

/** A grant in force at `now`: started (allowing for clock skew), not expired, not revoked. */
export function isGrantActive(grant: SupportAccessGrant, now: number): boolean {
  return (
    grant.revokedAt === null &&
    Date.parse(grant.startsAt) <= now + CLOCK_SKEW_MS &&
    Date.parse(grant.expiresAt) > now
  );
}

/** The support access explanation for a refused read on this organization (AdminProblem supportAccess). */
export function useSupportAccessHint(): { canRequest: boolean; requestPath: string } | undefined {
  const scope = useOrganizationScope();
  return scope.mayNeedSupportAccess
    ? { canRequest: scope.abilities.requestSupportAccess, requestPath: scope.tabPath('support-access') }
    : undefined;
}
